import { useMemo, useState } from 'react';
import type { AppRecord } from '../../lib/types';
import type { CompassLocale } from '../i18n';
import {
  buildCompassHref,
  getCompassCopy,
} from '../i18n';
import {
  deriveAppCategory,
  deriveAppTags,
  formatRunDate,
  isDemoAvailable,
  sortApps,
  type AppSort,
} from '../lib/catalogPresentation';

export function AppsGallery(props: {
  apps: AppRecord[];
  locale: CompassLocale;
}): React.JSX.Element {
  const { apps, locale } = props;
  const copy = getCompassCopy(locale);
  const text = locale === 'zh-CN'
    ? {
        title: '所有 AI App',
        description: '按类别、质量信号与可运行状态浏览 Compass 中的生成应用，快速定位可复用的工作流结果。',
        search: '搜索应用、标签或摘要...',
        all: '全部',
        sort: '排序',
        latest: '最新',
        name: '名称',
        score: '评分',
        demo: 'Demo',
        details: '详情',
        loadMore: '查看更多模型',
        created: '创建时间',
      }
    : {
        title: 'All AI Apps',
        description: 'Browse Compass apps by category, readiness, and quality signals to find reusable workflow outputs quickly.',
        search: 'Search by app, tag, or summary...',
        all: 'All',
        sort: 'Sort',
        latest: 'Latest',
        name: 'Name',
        score: 'Score',
        demo: 'Demo',
        details: 'Details',
        loadMore: 'Load More Models',
        created: 'Created',
      };

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(text.all);
  const [sort, setSort] = useState<AppSort>('score');

  const categories = useMemo(() => {
    return [text.all, ...Array.from(new Set(apps.map((app) => deriveAppCategory(app))))];
  }, [apps, text.all]);

  const filteredApps = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sortApps(apps, sort).filter((app) => {
      const appCategory = deriveAppCategory(app);
      const matchesCategory = category === text.all || category === appCategory;
      if (!matchesCategory) return false;
      if (!query) return true;
      return [
        app.name,
        app.summary,
        app.appId,
        appCategory,
        ...deriveAppTags(app),
      ].join(' ').toLowerCase().includes(query);
    });
  }, [apps, category, search, sort, text.all]);

  return (
    <main className="flex-grow pt-[100px] pb-12 px-4 md:px-10 max-w-[1280px] mx-auto w-full flex flex-col gap-12">
      <header className="text-center md:text-left flex flex-col gap-4 max-w-3xl">
        <h1 className="font-geist text-[48px] leading-[1.1] font-bold text-on-surface">{text.title}</h1>
        <p className="text-lg leading-relaxed text-on-surface-variant">{text.description}</p>
      </header>

      <section className="glass-card rounded-2xl p-6 flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <label className="w-full lg:max-w-md flex items-center gap-3 rounded-full border border-outline-variant/30 bg-surface-container px-4 py-3">
            <span className="material-symbols-outlined text-outline">search</span>
            <input
              className="w-full bg-transparent text-on-surface outline-none placeholder:text-outline-variant"
              placeholder={text.search}
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="scrollbar-hidden overflow-x-auto pb-1">
              <div className="flex w-max gap-2 pr-2">
                {categories.map((item) => {
                  const active = item === category;
                  return (
                    <button
                      key={item}
                      className={active
                        ? 'btn-primary-gradient px-4 py-2 rounded-full text-xs uppercase tracking-widest whitespace-nowrap'
                        : 'px-4 py-2 rounded-full text-xs uppercase tracking-widest whitespace-nowrap border border-outline-variant/40 text-on-surface-variant hover:text-on-surface hover:border-outline cursor-pointer transition-colors'}
                      onClick={() => setCategory(item)}
                      type="button"
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="flex items-center justify-end gap-2 text-sm text-on-surface-variant shrink-0">
              <span>{text.sort}</span>
              <select
                className="rounded-full border border-outline-variant/40 bg-surface-container px-4 py-2 text-on-surface outline-none"
                value={sort}
                onChange={(event) => setSort(event.target.value as AppSort)}
              >
                <option value="latest">{text.latest}</option>
                <option value="name">{text.name}</option>
                <option value="score">{text.score}</option>
              </select>
            </label>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {filteredApps.map((app, index) => {
          const previewHref = app.preview.url || buildCompassHref(`/apps/${app.appId}`, locale);
          const detailHref = buildCompassHref(`/apps/${app.appId}`, locale);
          const tags = deriveAppTags(app);
          const createdLabel = formatRunDate(app.sourceRunId, locale);
          return (
            <article
              key={app.appId}
              className={`glass-card rounded-2xl p-4 flex flex-col gap-4 group ${index % 5 === 2 ? 'xl:col-span-2' : ''}`}
            >
              <div className="relative overflow-hidden rounded-xl h-40 bg-surface-container-highest">
                {app.screenshots[0]?.publishedUrl ? (
                  <img
                    alt={app.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    src={app.screenshots[0].publishedUrl}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-outline-variant">
                    <span className="material-symbols-outlined text-5xl">dashboard</span>
                  </div>
                )}
                <div className="absolute top-3 right-3 rounded bg-surface-container-highest/85 px-2 py-1 text-[10px] uppercase tracking-widest text-cyan">
                  {copy.scoreLabel(app.statuses.harnessScore ?? 0)}
                </div>
              </div>

              <div className="flex flex-col gap-2 flex-grow">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-geist text-2xl font-semibold text-on-surface">{app.name}</h3>
                  <span className="rounded-full bg-surface-container-highest px-3 py-1 text-xs text-on-surface">
                    {deriveAppCategory(app)}
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-on-surface-variant line-clamp-6 flex-grow">{app.summary}</p>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-surface-bright px-3 py-1 text-[11px] text-on-surface-variant">
                      {tag}
                    </span>
                  ))}
                </div>
                {createdLabel && (
                  <div className="font-mono text-xs text-outline-variant">{text.created}: {createdLabel}</div>
                )}
              </div>

              <div className="mt-auto flex gap-2 pt-2 border-t border-outline-variant/20">
                <a
                  className="btn-primary-gradient flex-1 py-2 text-center rounded-full text-xs uppercase tracking-widest no-underline"
                  href={previewHref}
                  target={isDemoAvailable(app) && app.preview.url ? '_blank' : undefined}
                >
                  {text.demo}
                </a>
                <a
                  className="btn-secondary-cyan flex-1 py-2 text-center rounded-full text-xs uppercase tracking-widest no-underline"
                  href={detailHref}
                >
                  {text.details}
                </a>
              </div>
            </article>
          );
        })}
      </section>

      <div className="flex justify-center">
        <button className="btn-secondary-cyan px-8 py-3 rounded-full text-xs uppercase tracking-widest flex items-center gap-2" type="button">
          {text.loadMore}
          <span className="material-symbols-outlined text-[18px]">arrow_downward</span>
        </button>
      </div>
    </main>
  );
}
